import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";

import "./index.css";
import App from "./App";
import store from "./app/redux/store";
import "./app/assets/css/toi/common.css";

const root = ReactDOM.createRoot(document.getElementById("root"));

// AWS CloudWatch RUM (production only: performance + JS errors + HTTP)
// Lazy-load it to keep the main bundle smaller and reduce iOS startup cost.
// - iOS WebView: start only after native `viewAppear`
// - Android WebView (and others): start immediately (deferred to idle)
try {
  const isProdEnv = process.env.REACT_APP_ENV === "production";

  const isIosWebView = () =>
    typeof window !== "undefined" &&
    typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined";

  let rumStarted = false;
  let newRelicStarted = false;
  const startRum = () => {
    if (!isProdEnv || rumStarted) return;
    rumStarted = true;
    import("./app/libs/awsRum").then((m) => m.initAwsRum?.()).catch(() => {});
  };

  // Dynamically start New Relic (loader snippet is in public/index.html and should expose window.__startNewRelic).
  const startNewRelic = () => {
    if (!isProdEnv || newRelicStarted) return;
    newRelicStarted = true;
    try {
      // If index.html defines a deferred starter, call it; otherwise do nothing.
      window.__startNewRelic?.();
    } catch {
      // ignore
    }
  };

  if (isProdEnv) {
    const defer =
      window.__aboundDefer ||
      function (fn) {
        try {
          if ("requestIdleCallback" in window) {
            window.requestIdleCallback(fn, { timeout: 3000 });
          } else {
            window.addEventListener("load", fn);
          }
        } catch (e) {
          window.addEventListener("load", fn);
        }
      };

    if (isIosWebView()) {
      const onMessage = (evt) => {
        try {
          let payload = evt?.data;
          if (!payload) return;
          if (typeof payload === "string") payload = JSON.parse(payload);
          if (payload?.eventType === "viewAppear") {
            window.removeEventListener("message", onMessage);
            defer(() => {
              // startRum();
              startNewRelic();
            });
          }
        } catch (e) {
          // ignore
        }
      };
      window.addEventListener("message", onMessage);
      // Fallback: start RUM on first user interaction if viewAppear never came (e.g. old devices)
      const onInteraction = () => {
        if (!rumStarted || !newRelicStarted)
          defer(() => {
            // startRum();
            startNewRelic();
          });
      };
      ["click", "touchstart", "keydown"].forEach((event) => {
        window.addEventListener(event, onInteraction, { once: true });
      });
    } else {
      defer(() => {
        // startRum();
        startNewRelic();
      });
    }
  }
} catch {
  // ignore
}

// Microsoft Clarity (TOI only, production only)
// - iOS WebView: start only after native `viewAppear`
// - Android WebView (and others): start immediately (deferred to idle)
try {
  const clarityId = process.env.REACT_APP_CLARITY_PROJECT_ID || "";
  const isProdEnv = process.env.REACT_APP_ENV === "production";

  const getChannelSafe = () => {
    try {
      const fromSession = window?.sessionStorage?.getItem("channel") || "";
      if (fromSession) return fromSession;
    } catch (e) {
      // ignore
    }
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get("channel") || "";
    } catch (e) {
      return "";
    }
  };

  const isTOIChannel = () => getChannelSafe() === "TOI";

  const isIosWebView = () =>
    typeof window !== "undefined" &&
    typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined";

  let clarityStarted = false;
  const startClarity = () => {
    if (!clarityId  || clarityStarted) return;
    clarityStarted = true;
    import("./app/libs/clarity")
      .then((m) => m.initClarityForce?.(clarityId))
      .catch(() => {});
  };

  if (clarityId) {
    const defer =
      window.__aboundDefer ||
      function (fn) {
        try {
          if ("requestIdleCallback" in window) {
            window.requestIdleCallback(fn, { timeout: 3000 });
          } else {
            window.addEventListener("load", fn);
          }
        } catch (e) {
          window.addEventListener("load", fn);
        }
      };

    if (isIosWebView()) {
      const onMessage = (evt) => {
        try {
          let payload = evt?.data;
          if (!payload) return;
          if (typeof payload === "string") payload = JSON.parse(payload);
          if (payload?.eventType === "viewAppear") {
            window.removeEventListener("message", onMessage);
            defer(startClarity);
          }
        } catch (e) {
          // ignore
        }
      };
      window.addEventListener("message", onMessage);
      // Fallback: start Clarity on first user interaction if viewAppear never came (e.g. old devices)
      const onInteraction = () => {
        if (!clarityStarted) defer(startClarity);
      };
      ["click", "touchstart", "keydown"].forEach((event) => {
        window.addEventListener(event, onInteraction, { once: true });
      });
    } else {
      defer(startClarity);
    }
  }
} catch {
  // ignore
}

root.render(
  <Provider store={store}>
    <App />
  </Provider>,
);

if (process.env.REACT_APP_ENV !== "production") {
  // reportWebVitals(console.log);
}
